"use client"

import { ViewTransition } from "react"
import { Fragment, useState } from "react"
import { usePathname } from "next/navigation"

/**
 * App-like directional page transitions.
 *
 * Wraps the routed content in a single persistent <ViewTransition>. A route swap
 * is an `update`, so the old and new screen snapshots share ONE direction class —
 * that keeps both halves of the slide consistent (a keyed enter/exit pair would
 * let the exiting screen keep its own stale class). Direction is inferred from a
 * path stack rather than per-link transition types — this way forward `push`,
 * in-app `router.back()`, and the OS swipe-back gesture all animate correctly
 * (router.back() === history.back(), which can't carry a transition type).
 *
 * The inner keyed <Fragment> is what makes BACK animate. Forward navigations
 * build a fresh element tree, so the boundary's content changes and `update`
 * fires. Back restores Next's *cached* route tree (same element refs) — without a
 * changing key React sees no content change and skips the transition. Re-keying
 * by pathname forces the children to remount on every navigation, so `update`
 * fires in both directions.
 *
 * React only fires view transitions inside a Transition (route navigations are
 * Transitions); plain in-page setState does not animate — so the direction class
 * lingering between navigations is harmless.
 *
 * - forward (deeper / new screen) → old slides left, new slides in from right
 * - back (returning to a prior screen) → old slides right, new slides in from left
 * - lateral (bottom-nav tab ↔ tab, sign-in ↔ sign-up) → quick crossfade, no directional slide
 * - initial load → no animation
 *
 * CSS lives in app/globals.css (::view-transition-* rules + keyframes), and
 * honours prefers-reduced-motion there.
 */
export function PageTransition({ children }: { children: React.ReactNode }) {
  const pathname = usePathname()

  // The path stack + the class for the in-flight navigation. We derive the next
  // value during render (the endorsed "adjust state on prop change" pattern) so
  // the class is set in the same commit that swaps the children — that's the
  // commit the View Transition snapshots. Guarded by the top-of-stack check, so
  // it settles after one extra render and never loops.
  const [nav, setNav] = useState<NavState>(() => ({
    stack: [pathname],
    dir: "vt-none",
  }))
  if (nav.stack[nav.stack.length - 1] !== pathname) {
    setNav((prev) => advance(prev, pathname))
  }

  return (
    <ViewTransition enter="vt-none" update={nav.dir}>
      <Fragment key={pathname}>{children}</Fragment>
    </ViewTransition>
  )
}

function advance(prev: NavState, pathname: string): NavState {
  const { stack } = prev
  const top = stack[stack.length - 1]

  // Splash → destination: opacity bloom on the page while the named
  // `psc-logo` + `psc-backdrop` groups morph in place. See app/globals.css.
  if (top === "/" && pathname !== "/") {
    return { stack: [...stack, pathname], dir: "vt-bloom" }
  }
  // Sign-in ↔ sign-up is a segmented control, not a screen change. Measured, a
  // route-level transition here holds the snapshot overlay open ~800ms (far
  // past its 160ms crossfade) and every tap in that window hits the layout
  // wrapper instead of the tab, so the control feels frozen. "none" opts out.
  if (AUTH_TABS.has(pathname) && AUTH_TABS.has(top)) {
    return { stack: [pathname], dir: "none" }
  }
  // Lateral tab switch — depth would falsely imply spatial hierarchy.
  if (TAB_ROOTS.has(pathname) && TAB_ROOTS.has(top)) {
    return { stack: [pathname], dir: "vt-fade" }
  }
  // Returning to the previous screen (in-app back or swipe-back).
  if (stack[stack.length - 2] === pathname) {
    return { stack: stack.slice(0, -1), dir: "vt-back" }
  }
  // A new / deeper screen.
  return { stack: [...stack, pathname], dir: "vt-forward" }
}

// "none" is React's own opt-out value — no transition runs, no overlay.
// "vt-none" is a real class whose CSS sets animation: none (transition still runs).
type TransitionClass =
  | "vt-forward"
  | "vt-back"
  | "vt-fade"
  | "vt-bloom"
  | "vt-none"
  | "none"

type NavState = { stack: string[]; dir: TransitionClass }

// Top-level bottom-nav destinations — navigation between these is lateral.
const TAB_ROOTS = new Set(["/home", "/book", "/bookings", "/profile"])

// Sign-in/sign-up act as a segmented tab control, not a depth change — a
// directional slide there would run a full view transition on every toggle,
// whose snapshot overlay can eat the next tap before it clears.
const AUTH_TABS = new Set(["/sign-in", "/sign-up"])
